import React from 'react';
import { useSelector } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { formatoPeru } from '../../../helpers/commons';

const useStyles = makeStyles(() => ({
    detalleRoot: {
        fontFamily: '"Segoe UI", Roboto, sans-serif',
        fontSize: '12px',
        color: '#1A2B4A',
    },

    // ── Tabla de campos — desktop ────────────────────────────────────────────
    infoTabla: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '10px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #C8D6EE',
        tableLayout: 'fixed',

        // En mobile: ocultar la tabla y mostrar el layout apilado
        '@media (max-width: 599px)': {
            display: 'none',
        },
    },
    infoLabel: {
        backgroundColor: '#D6E4FF',
        color: '#003399',
        fontWeight: 700,
        fontSize: '10px',
        padding: '5px 6px',
        border: '1px solid #C8D6EE',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        verticalAlign: 'middle',
    },
    infoValor: {
        backgroundColor: '#FFFFFF',
        color: '#1A2B4A',
        fontSize: '11px',
        fontWeight: 600,
        padding: '5px 8px',
        border: '1px solid #C8D6EE',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    },

    // ── Layout apilado — solo mobile ─────────────────────────────────────────
    infoMobile: {
        display: 'none',
        '@media (max-width: 599px)': {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginBottom: '10px',
            border: '1px solid #C8D6EE',
            borderRadius: '4px',
            overflow: 'hidden',
        },
    },
    infoMobileFila: {
        display: 'flex',
        borderBottom: '1px solid #C8D6EE',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    infoMobileLabel: {
        backgroundColor: '#D6E4FF',
        color: '#003399',
        fontWeight: 700,
        fontSize: '10px',
        padding: '5px 7px',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        width: '100px',
        display: 'flex',
        alignItems: 'center',
        borderRight: '1px solid #C8D6EE',
    },
    infoMobileValor: {
        backgroundColor: '#FFFFFF',
        color: '#1A2B4A',
        fontSize: '11px',
        fontWeight: 600,
        padding: '5px 8px',
        flex: 1,
        wordBreak: 'break-word',
        display: 'flex',
        alignItems: 'center',
    },

    // ── Badge de estado ───────────────────────────────────────────────────────
    estadoPendiente: {
        display: 'inline-block',
        backgroundColor: '#E8EFFF',
        color: '#0033CC',
        fontSize: '10px',
        fontWeight: 700,
        padding: '1px 8px',
        borderRadius: '10px',
        border: '1px solid #99AAEE',
        whiteSpace: 'nowrap',
    },
    estadoAprobado: {
        display: 'inline-block',
        backgroundColor: '#E5F6EC',
        color: '#1A7F3C',
        fontSize: '10px',
        fontWeight: 700,
        padding: '1px 8px',
        borderRadius: '10px',
        border: '1px solid #90D4A8',
        whiteSpace: 'nowrap',
    },
    estadoRechazado: {
        display: 'inline-block',
        backgroundColor: '#FDECEA',
        color: '#C0392B',
        fontSize: '10px',
        fontWeight: 700,
        padding: '1px 8px',
        borderRadius: '10px',
        border: '1px solid #F0A89F',
        whiteSpace: 'nowrap',
    },
    estadoRevisado: {
        display: 'inline-block',
        backgroundColor: '#FEF8E7',
        color: '#9A6D00',
        fontSize: '10px',
        fontWeight: 700,
        padding: '1px 8px',
        borderRadius: '10px',
        border: '1px solid #E8CC80',
        whiteSpace: 'nowrap',
    },

    // ── Tabla de productos ────────────────────────────────────────────────────
    productosWrapper: {
        backgroundColor: '#FFFFFF',
        border: '1px solid #C8D6EE',
        overflow: 'hidden',
        marginTop: '4px',
    },
    productosTabla: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '11px',
        tableLayout: 'fixed',
    },
    productosTh: {
        backgroundColor: '#003399',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: '11px',
        padding: '7px 10px',
        textAlign: 'left',
        letterSpacing: '0.2px',
    },
    productosTd: {
        padding: '6px 10px',
        borderBottom: '1px solid #E8EEF8',
        color: '#2C3E6B',
        fontSize: '11px',
        verticalAlign: 'middle',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    productosTdDer: {
        padding: '6px 10px',
        borderBottom: '1px solid #E8EEF8',
        color: '#2C3E6B',
        fontSize: '11px',
        textAlign: 'right',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
    },
    filaPar:   { backgroundColor: '#F4F8FF' },
    filaImpar: { backgroundColor: '#FFFFFF' },
    filaTotal: {
        backgroundColor: '#EEF3FF',
        borderTop: '2px solid #C8D6EE',
    },
    totalCelda: {
        padding: '7px 10px',
        textAlign: 'right',
        fontSize: '12px',
        fontWeight: 700,
        color: '#003399',
    },
    loaderCelda: {
        padding: '16px',
        textAlign: 'center',
    },
    sinRegistros: {
        textAlign: 'center',
        padding: '14px',
        color: '#7A90B8',
        fontSize: '11px',
        fontStyle: 'italic',
    },
}));

function BadgeEstado({ estado, classes }) {
    const mapa = {
        P: { cls: classes.estadoPendiente, txt: 'PENDIENTE' },
        A: { cls: classes.estadoAprobado,  txt: 'APROBADO'  },
        R: { cls: classes.estadoRechazado, txt: 'RECHAZADO' },
    };
    const cfg = mapa[estado] ?? { cls: classes.estadoRevisado, txt: 'REVISADO' };
    return <span className={cfg.cls}>{cfg.txt}</span>;
}

function DetalleAprobacionPedidoSpot() {
    const classes    = useStyles();
    const loading    = useSelector((s) => s.bandejaaprobacion.loading);
    const selected   = useSelector((s) => s.bandejaaprobacion.store.selectedPedidoSpot);
    const pedidoSpot = useSelector((s) => s.bandejaaprobacion.store.detallepedidoSpot);

    if (!selected?.length) return null;

    const total = pedidoSpot.detalles?.reduce(
        (acc, item) => acc + item.cantidad * item.precioFinal, 0
    ) ?? 0;

    // Filas de info — fuente única de verdad usada tanto en desktop como mobile
    const filasInfo = [
        { label: 'Cód. Pedido', valor: pedidoSpot.codigo },
        { label: 'Estado',      valor: <BadgeEstado estado={pedidoSpot.estado} classes={classes} /> },
        { label: 'Fecha',       valor: pedidoSpot.fecha },
        { label: 'NIT',         valor: pedidoSpot.nit },
        { label: 'Cliente',     valor: pedidoSpot.cliente?.toUpperCase() },
        { label: 'L. Crédito',  valor: formatoPeru('en-US', 'USD', pedidoSpot.lineaCredito) },
        { label: 'Vendedor',    valor: pedidoSpot.vendedor?.toUpperCase() },
        ...(pedidoSpot.observacion
            ? [{ label: 'Observación', valor: pedidoSpot.observacion?.toUpperCase(), multilinea: true }]
            : []),
        ...(pedidoSpot.estado === 'R'
            ? [{ label: 'Motivo Rechazo', valor: pedidoSpot.motivoRechazo?.toUpperCase(), multilinea: true }]
            : []),
    ];

    return (
        <div className={classes.detalleRoot}>

            {/* ── Desktop: tabla de 6 columnas (label|valor x3 por fila) ── */}
            <table className={classes.infoTabla}>
                <colgroup>
                    <col style={{ width: '110px' }} />
                    <col style={{ width: 'auto'   }} />
                    <col style={{ width: '70px'  }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '55px'  }} />
                    <col style={{ width: '110px' }} />
                </colgroup>
                <tbody>
                    <tr>
                        <td className={classes.infoLabel}>Cód. Pedido</td>
                        <td className={classes.infoValor}>{pedidoSpot.codigo}</td>
                        <td className={classes.infoLabel}>Estado</td>
                        <td className={classes.infoValor}>
                            <BadgeEstado estado={pedidoSpot.estado} classes={classes} />
                        </td>
                        <td className={classes.infoLabel}>Fecha</td>
                        <td className={classes.infoValor}>{pedidoSpot.fecha}</td>
                    </tr>
                    <tr>
                        <td className={classes.infoLabel}>NIT</td>
                        <td className={classes.infoValor}>{pedidoSpot.nit}</td>
                        <td className={classes.infoLabel}>Cliente</td>
                        <td className={classes.infoValor} colSpan="3">{pedidoSpot.cliente?.toUpperCase()}</td>
                    </tr>
                    <tr>
                        <td className={classes.infoLabel}>L. Crédito</td>
                        <td className={classes.infoValor}>{formatoPeru('en-US', 'USD', pedidoSpot.lineaCredito)}</td>
                        <td className={classes.infoLabel}>Vendedor</td>
                        <td className={classes.infoValor} colSpan="3">{pedidoSpot.vendedor?.toUpperCase()}</td>
                    </tr>
                    {pedidoSpot.observacion && (
                        <tr>
                            <td className={classes.infoLabel}>Observación</td>
                            <td className={classes.infoValor} colSpan="5" style={{ whiteSpace: 'normal' }}>
                                {pedidoSpot.observacion?.toUpperCase()}
                            </td>
                        </tr>
                    )}
                    {pedidoSpot.estado === 'R' && (
                        <tr>
                            <td className={classes.infoLabel}>Motivo Rechazo</td>
                            <td className={classes.infoValor} colSpan="5" style={{ whiteSpace: 'normal' }}>
                                {pedidoSpot.motivoRechazo?.toUpperCase()}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* ── Mobile: cada campo en su propia fila apilada ── */}
            <div className={classes.infoMobile}>
                {filasInfo.map(({ label, valor, multilinea }) => (
                    <div key={label} className={classes.infoMobileFila}>
                        <div className={classes.infoMobileLabel}>{label}</div>
                        <div
                            className={classes.infoMobileValor}
                            style={multilinea ? { whiteSpace: 'normal' } : {}}
                        >
                            {valor}
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Tabla de productos ── */}
            <div className={classes.productosWrapper}>
                <table className={classes.productosTabla}>
                    <colgroup>
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '50%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={classes.productosTh}>Código</th>
                            <th className={classes.productosTh}>Descripción</th>
                            <th className={classes.productosTh} style={{ textAlign: 'right' }}>Cant.</th>
                            <th className={classes.productosTh} style={{ textAlign: 'right' }}>Precio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="4" className={classes.loaderCelda}>
                                    <img
                                        alt="Cargando..."
                                        src="https://app.iess.gob.ec/iess-fondo-reserva-afiliado-web/javax.faces.resource/images/load.gif.jsf;jsessionid=8949b76cfea43d283add3bd3dd2d?ln=sentinel-layout"
                                        width="32" height="32"
                                    />
                                </td>
                            </tr>
                        ) : pedidoSpot.detalles?.length > 0 ? (
                            <>
                                {pedidoSpot.detalles.map((item, i) => (
                                    <tr key={i} className={i % 2 === 0 ? classes.filaPar : classes.filaImpar}>
                                        <td className={classes.productosTd}>{item.codigoProducto}</td>
                                        <td className={classes.productosTd}>{item.dProducto}</td>
                                        <td className={classes.productosTdDer}>{item.cantidad}</td>
                                        <td className={classes.productosTdDer}>
                                            {formatoPeru('en-US', 'USD', item.precioFinal)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className={classes.filaTotal}>
                                    <td colSpan="4" className={classes.totalCelda}>
                                        Total: {formatoPeru('en-US', 'USD', total)}
                                    </td>
                                </tr>
                            </>
                        ) : (
                            <tr>
                                <td colSpan="4" className={classes.sinRegistros}>
                                    No existe registro de productos
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
}

export default DetalleAprobacionPedidoSpot;